import ISOCHRONOUS_TYPES from '@core/constants/isochronous/types';
import IsochronousColors from '@core/theme/map.colors';
import { binarySearch } from '@core/utils/searchAlgorithms/binarySearch';
import produce from 'immer';

export const limit = 50;
// Maximum amount of vertices a polygon can have while still
// being considered editable on the map. More complex polygons
// become read-only to avoid performance issues when interacting
// with the Google Maps polygon editor.
export const MAX_EDITABLE_VERTICES = 150;
export const defaultPolygons = { defaultShow: true, visible: new Set(), editable: new Set(), polygons: {} };
export const defaultCircles = { defaultShow: true, visible: new Set(), editable: new Set(), circles: {} };
export const getCircleBounds = (center, radius) =>
	new window.google.maps.Circle({ center, radius }).getBounds();
/**
 * Formats the isochronous for the map
 *
 * @param {*} iso - Isochronous to format
 * @param {*} onChange - onChange action to add to the isochronous
 * @returns
 */
export const handleIsochronousToMapObject = (iso, onChange) => {
	const { id, vertices, center, radius, address } = iso;
  const isEditable = !vertices || vertices.length <= MAX_EDITABLE_VERTICES;
	return {
		id,
		info: iso,
		options: {
			center, radius, address, path: vertices,
			strokeColor: IsochronousColors[id % IsochronousColors.length],
			fillColor: IsochronousColors[id % IsochronousColors.length],
			onChangeRadius: onChange('radius'), onChangeCenter: onChange('center'), onChangeVertices: onChange('vertices'),
		},
	};
};
/**
 * Handles the formatting of multiple isochronous to add them to
 * the map
 */
export const handleMultipleIsochronousToMapObjects = (data, onChange, customIsochronousToMap) => {
	const poly = {};
	const circ = {};
	data.forEach((iso) => {
		const isochronous = customIsochronousToMap
			? customIsochronousToMap(iso, onChange)
			: handleIsochronousToMapObject(iso, onChange);
		if (ISOCHRONOUS_TYPES.CIRCLE === iso.type) {
			circ[iso.id] = isochronous;
		} else if (ISOCHRONOUS_TYPES.POLYGON === iso.type) poly[iso.id] = isochronous;
	});
	return { polygons: poly, circles: circ };
};
/**
 * Gets the bounds of the isochronous to show the map with the data centered
 * @param {*} param0
 * @param {*} fitBounds
 */
export const handleFitIsochronousBounds = ({ polygons, circles }, fitBounds) => {
	const bounds = new window.google.maps.LatLngBounds();
	if (polygons) {
		polygons.forEach((polygon) => {
			const { vertices } = polygon;
			if (vertices && vertices.length > 0)
        vertices.forEach((vertex) => bounds.union(new window.google.maps.LatLngBounds(vertex)));
		});
	}
	if (circles) {
		circles.forEach((circle) => {
			const { radius, center } = circle;
			if (radius && center) bounds.union(getCircleBounds(center, radius));
		});
	}
	fitBounds(bounds);
};
/**
 * Handles the update of the coordinates for a polygon isochronous
 * when the location changes
 * @param {*} id
 * @param {*} updatedLocation
 * @param {*} mapElements
 * @returns
 */
const updatePolygonLocation = (id, updatedLocation, mapElements) =>
	produce(mapElements, (draftElements) => {
		const elementOptions = draftElements.polygons.polygons[id].options;
		const newVertices = elementOptions.path.map((vertex) => {
			// Get the difference from the center
			const susLat = vertex.lat - elementOptions.center.lat;
			const susLng = vertex.lng - elementOptions.center.lng;
			// Add the difference from the center to the new center
			return { lat: updatedLocation.center.lat + susLat, lng: updatedLocation.center.lng + susLng };
		});
		elementOptions.center = updatedLocation.center;
		elementOptions.path = newVertices;
	});
/**
 * Handles the update of the coordinates from a circle isochronous
 * when its data changes
 * @param {number} id
 * @param {coordinates} updatedLocation
 * @param {*} mapElements
 * @returns
 */
const updateCirclesLocation = (id, updatedLocation, mapElements) =>
	produce(mapElements, (draftElements) => {
		const elementOptions = draftElements.circles.circles[id].options;
		elementOptions.center = updatedLocation.center;
		elementOptions.radius = updatedLocation.radius;
	});
/**
 * Handles the location update of an isochronous
 * @param {*} isochronous
 * @param {*} updatedLocation
 * @param {*} mapElements
 * @returns
 */
export const updateIsochronousLocation = (isochronous, updatedLocation, mapElements) => {
	const { id, type } = isochronous;
	if (type === ISOCHRONOUS_TYPES.CIRCLE) {
		// If not found in circles, reset to the original in baseIsochronous
		if (!(id in mapElements.circles.circles)) {
			const isochronous = binarySearch(mapElements.baseIsochronous, id, {reversed: true});
			if (isochronous === -1) return mapElements
			mapElements = produce(mapElements, (draft) => {
				draft.circles.circles[id] = draft.polygons.polygons[id];
				draft.circles.circles[id].options.center = isochronous.center;
				draft.circles.circles[id].options.radius = isochronous.radius;
				draft.circles.circles[id].options.address = isochronous.address;
				draft.circles.circles[id].options.path = [];
				// Remove if stored inside polygons (in the case the isochronous was half edited and changed)
				if (id in draft.polygons.polygons) delete draft.polygons.polygons[id];})
		}
		const { options } = mapElements.circles.circles[id];
		if (
			options.center.lat !== updatedLocation.center.lat ||
			options.center.lng !== updatedLocation.center.lng ||
			options.radius !== updatedLocation.radius
		) return updateCirclesLocation(id, updatedLocation, mapElements);
	} else if (type === ISOCHRONOUS_TYPES.POLYGON) {
		// If not found in polygons, reset to the original in baseIsochronous
		if (!(id in mapElements.polygons.polygons)) {
			const isochronous = binarySearch(mapElements.baseIsochronous, id, {reversed: true});
			if (isochronous === -1) return mapElements
			mapElements = produce(mapElements, (draft) => {
				draft.polygons.polygons[id] = draft.circles.circles[id];
				draft.polygons.polygons[id].options.center = isochronous.center;
				draft.polygons.polygons[id].options.radius = isochronous.radius;
				draft.polygons.polygons[id].options.address = isochronous.address;
				draft.polygons.polygons[id].options.path = isochronous.path;
				// Remove if stored inside polygons (in the case the isochronous was half edited and changed)
				if (id in draft.circles.circles) delete draft.circles.circles[id];
			})
		}
		const { options } = mapElements.polygons.polygons[id];
		if (options.center.lat !== updatedLocation.center.lat || options.center.lng !== updatedLocation.center.lng)
      return updatePolygonLocation(id, updatedLocation, mapElements);
	}
	return null;
};
/**
 * Sets all the elements to show by default and clears
 * the set containing the isochronous to be visible/editable
 *
 * @param {*} circles
 * @param {*} polygons
 */
export const clearSelectedIsochronous = (circles, polygons) => {
	circles.defaultShow = true;
	polygons.defaultShow = true;
	circles.visible = new Set();
	circles.editable = new Set();
	polygons.visible = new Set();
	polygons.editable = new Set();
};
/**
 * Sets an isochronous to be visible in their corresponding object
 * (either polygons or circles)
 * @param {*} editable
 * @param {*} notEditable
 * @param {*} isochronous
 */
export const makeIsochronousVisibleAndEditable = (editable, notEditable, isochronous) => {
	if (Array.isArray(isochronous)) {
		editable.visible = new Set(isochronous);
		editable.editable = new Set(isochronous);
	} else {
		editable.visible = new Set([isochronous]);
		editable.editable = new Set([isochronous]);
	}
	notEditable.visible = new Set();
	notEditable.editable = new Set();
};
